import * as THREE from 'three';
import { DynamicObject, UpdatePriority } from './DynamicObject';
import { LODLevel } from '../../optimization/LODSystem';
import { LODToaster } from './LODToaster';
// import { FruitProjectile } from './FruitProjectile'; // Removed until needed
import { ObjectManager } from '../ObjectManager';
import { BoundingSphere } from '../../physics/BoundingVolume';
import { ProjectileSystem, ProjectileType } from '../../projectiles';
import { Placeable } from '../Placeable';

/**
 * BlenderTower - a defense tower with a rotating orb and gun barrel
 * that targets and shoots at enemy toasters when they enter its radius
 */
export class BlenderTower extends DynamicObject implements Placeable {
  // Tower properties
  private targetingRadius: number = 15;
  private shootingInterval: number = 0.05; // seconds between shots
  private lastShotTime: number = 0;
  private currentTarget: LODToaster | null = null;
  private orbRotationHorizontal: number = 0;
  private orbRotationVertical: number = 0; // Start at 0 degrees (horizontal)
  private targetRotationHorizontal: number = 0;
  private targetRotationVertical: number = 0;
  private rotationSpeed: number = 4.0; // Radians per second
  private projectileSpeed: number = 60; // Units per second
  private projectileDamage: number = 50;
  private placementSize: number = 3.0; // Size used for placement restrictions

  // Root object to hold all LOD versions
  private rootObject!: THREE.Mesh;
  
  // LOD meshes
  private highDetailGroup!: THREE.Group;
  private mediumDetailGroup!: THREE.Group;
  private lowDetailGroup!: THREE.Group;
  
  // Orb references for rotation
  private highDetailOrb!: THREE.Group;
  private mediumDetailOrb!: THREE.Group;
  private lowDetailOrb!: THREE.Group;

  // Shared references
  private objectManager: ObjectManager;
  private projectileSystem: ProjectileSystem | null = null;

  constructor(objectManager: ObjectManager, projectileSystem: ProjectileSystem | null = null, name: string = 'BlenderTower') {
    super(name);

    // Store the object manager reference directly
    this.objectManager = objectManager;
    
    // Store the projectile system if provided
    this.projectileSystem = projectileSystem;
    console.log(`BlenderTower ${name} constructed with projectileSystem: ${projectileSystem ? 'Yes' : 'No'}`);

    // Set update priority
    this.setUpdatePriority(UpdatePriority.MEDIUM);

    // Create tower mesh
    this.createTowerMesh();

    // Enable LOD
    this.enableLOD();
    
    // Enable shadows for the tower
    const mesh = this.getMesh();
    if (mesh) {
      mesh.castShadow = true;
      mesh.receiveShadow = true;
    }
    
    // Set a wider bounding volume to keep enemies at a distance
    this.setBoundingVolume(new BoundingSphere(4.0));
    
    console.log(`BlenderTower created: ${this.getName()}`);
  }

  /**
   * Implement the Placeable interface
   * Returns the placement size (radius) of the tower
   */
  public getPlacementSize(): number {
    return this.placementSize;
  }

  /**
   * Set the projectile system for this tower
   * @param projectileSystem The projectile system to use
   */
  public setProjectileSystem(projectileSystem: ProjectileSystem): void {
    this.projectileSystem = projectileSystem;
  }

  /**
   * Create the tower mesh with all components
   */
  private createTowerMesh(): void {
    // Create a root mesh that will be the actual GameObject mesh
    // Using BoxGeometry with near-zero size as a simple placeholder
    const rootGeometry = new THREE.BoxGeometry(0.1, 0.1, 0.1);
    const rootMaterial = new THREE.MeshBasicMaterial({ visible: false });
    this.rootObject = new THREE.Mesh(rootGeometry, rootMaterial);
    
    // Set the root object as the mesh for the GameObject system
    this.setMesh(this.rootObject);
    
    // Create all detail levels with the same configuration
    const segments = {
      high: 10,
      medium: 8,
      low: 6
    };

    // Create each detail level
    this.highDetailGroup = this.createDetailLevel(segments.high, true);
    this.rootObject.add(this.highDetailGroup);
    
    this.mediumDetailGroup = this.createDetailLevel(segments.medium, false);
    this.mediumDetailGroup.visible = false;
    this.rootObject.add(this.mediumDetailGroup);
    
    this.lowDetailGroup = this.createDetailLevel(segments.low, false);
    this.lowDetailGroup.visible = false;
    this.rootObject.add(this.lowDetailGroup);
    
    // Enable shadows for all components
    this.rootObject.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        object.castShadow = true;
        object.receiveShadow = true;
      }
    });
    
    // For LOD, we'll use our own visibility toggling in setLODLevel
    this.setLODLevel(LODLevel.HIGH);
  }

  /**
   * Create a detail level version of the tower
   * @param segments Number of segments for curved geometries
   * @param isHighDetail Whether this is the high detail version (to save references)
   */
  private createDetailLevel(segments: number, isHighDetail: boolean): THREE.Group {
    // Each detail level gets its own complete, identical structure
    const group = new THREE.Group();

    // Fixed measurements to ensure consistency across LOD levels
    const poleHeight = 6.0;
    const poleRadius = 0.4;
    const orbRadius = 2.0;
    const barrelLength = 3.0;
    const barrelRadius = 0.2;
    const orbPositionY = poleHeight - 0.2; // Fixed position for orb

    // Colors
    const poleColor = 0x555555; // Gunmetal gray
    const orbColor = 0xfff54e;  // Yellow
    const barrelColor = 0x222222; // Dark gray/black

    // Materials - creating new materials for each LOD level to ensure no shared state
    const poleMaterial = new THREE.MeshStandardMaterial({
      color: poleColor,
      roughness: 0.7,
      metalness: 0.8,
      flatShading: true
    });
    
    const orbMaterial = new THREE.MeshStandardMaterial({
      color: orbColor,
      roughness: 0.3,
      metalness: 0.2,
      flatShading: true
    });
    
    const barrelMaterial = new THREE.MeshStandardMaterial({
      color: barrelColor,
      roughness: 0.5,
      metalness: 0.7,
      flatShading: true
    });
    
    // 1. Create slim pole with exact positioning
    const poleGeometry = new THREE.CylinderGeometry(poleRadius, poleRadius, poleHeight, segments, 1);
    const towerPole = new THREE.Mesh(poleGeometry, poleMaterial);
    towerPole.position.y = poleHeight / 2;
    towerPole.castShadow = true;
    towerPole.receiveShadow = true;
    group.add(towerPole);
    
    // 2. Create the orb group (rotating part) at fixed position
    const orbGroup = new THREE.Group();
    orbGroup.position.y = orbPositionY;
    group.add(orbGroup);
    
    // Store reference to the appropriate orb group based on detail level
    if (isHighDetail) {
      this.highDetailOrb = orbGroup;
    } else if (segments === 8) {
      this.mediumDetailOrb = orbGroup;
    } else {
      this.lowDetailOrb = orbGroup;
    }
    
    // 3. Create low-poly orb with precise positioning
    const orbGeometry = new THREE.IcosahedronGeometry(orbRadius, 1);
    const orbHead = new THREE.Mesh(orbGeometry, orbMaterial);
    orbHead.position.y = 0; // Center the orb
    orbHead.castShadow = true;
    orbHead.receiveShadow = true;
    orbGroup.add(orbHead);
    
    // 4. Create gun barrel with exact measurements and positions
    const barrelGeometry = new THREE.CylinderGeometry(barrelRadius * 1.1, barrelRadius, barrelLength, segments, 1);
    const gunBarrel = new THREE.Mesh(barrelGeometry, barrelMaterial);
    
    // Position barrel at center of orb, pointing outward (along Z-axis)
    gunBarrel.rotation.x = -Math.PI / 2; // Rotate 90 degrees to point along Z-axis
    gunBarrel.position.z = barrelLength / 2; // Move backward half its length so narrow end points outward
    gunBarrel.castShadow = true;
    gunBarrel.receiveShadow = true;
    orbGroup.add(gunBarrel);

    return group;
  }

  /**
   * Set the LOD level and update the visible mesh
   */
  public override setLODLevel(level: LODLevel): void {
    // Call parent method which tracks LOD level
    super.setLODLevel(level);
    
    // Hide all detail levels
    this.highDetailGroup.visible = false;
    this.mediumDetailGroup.visible = false;
    this.lowDetailGroup.visible = false;
    
    // Show the active detail level
    switch (level) {
      case LODLevel.HIGH:
        this.highDetailGroup.visible = true;
        break;
      case LODLevel.MEDIUM:
        this.mediumDetailGroup.visible = true;
        break;
      case LODLevel.LOW:
        this.lowDetailGroup.visible = true;
        break;
    }
  }

  /**
   * Find the closest toaster in range
   */
  private findTargetInRange(): LODToaster | null {
    // Get all objects in radius
    const position = this.getPosition();
    const objectsInRange = this.objectManager.getObjectsInRadius(position, this.targetingRadius);
    
    // Find closest toaster
    let closestToaster: LODToaster | null = null;
    let closestDistance = this.targetingRadius * this.targetingRadius;
    
    for (const object of objectsInRange) {
      if (object instanceof LODToaster && object.isActiveState()) {
        const distanceSq = position.distanceToSquared(object.getPosition());
        if (distanceSq < closestDistance) {
          closestDistance = distanceSq;
          closestToaster = object;
        }
      }
    }
    
    return closestToaster;
  }

  /**
   * Rotate orb to face target
   */
  private rotateOrbTowardsTarget(target: LODToaster): void {
    // Calculate direction to target
    const targetPosition = target.getPosition();
    const towerPosition = this.getPosition();
    
    // Get target velocity and estimate future position
    const targetVelocity = target.getVelocity ? target.getVelocity() : new THREE.Vector3();
    
    // Only apply lead targeting if the target is moving at a significant speed
    if (targetVelocity && targetVelocity.lengthSq() > 0.1) {
      // Calculate time to reach target based on projectile speed and current distance
      const distance = towerPosition.distanceTo(targetPosition);
      const timeToTarget = distance / this.projectileSpeed;
      
      // Predict where the target will be when projectile arrives
      const predictedPosition = targetPosition.clone().add(
        targetVelocity.clone().multiplyScalar(timeToTarget * 0.8) // Compensate for target movement
      );
      
      // Use the predicted position for aiming instead of current position
      console.log(`Lead targeting: predicting target ${timeToTarget.toFixed(2)}s ahead`);
      
      // Adjust tower position to orb height
      towerPosition.y += 6.0 - 0.2; // poleHeight - 0.2 (orbPositionY)
      
      const direction = new THREE.Vector3()
        .subVectors(predictedPosition, towerPosition)
        .normalize();
      
      // Calculate horizontal angle (in radians) on the XZ plane
      this.targetRotationHorizontal = Math.atan2(direction.x, direction.z);
      
      // Calculate vertical angle (in radians)
      const horizontalDistance = Math.sqrt(direction.x * direction.x + direction.z * direction.z);
      // Negative because we want positive angles to point down
      let verticalAngle = -Math.atan2(direction.y, horizontalDistance);
      
      // Convert to our coordinate system and clamp between 90° down and 20° up
      const minVertical = -Math.PI / 9; // -20 degrees (pointing up)
      const maxVertical = Math.PI / 2; // 90 degrees (pointing down)
      this.targetRotationVertical = Math.max(minVertical, Math.min(maxVertical, verticalAngle));
    } else {
      // Standard targeting for stationary or slow targets
      // Adjust tower position to orb height
      towerPosition.y += 6.0 - 0.2; // poleHeight - 0.2 (orbPositionY)
      
      const direction = new THREE.Vector3()
        .subVectors(targetPosition, towerPosition)
        .normalize();
      
      // Calculate horizontal angle (in radians) on the XZ plane
      this.targetRotationHorizontal = Math.atan2(direction.x, direction.z);
      
      // Calculate vertical angle (in radians)
      const horizontalDistance = Math.sqrt(direction.x * direction.x + direction.z * direction.z);
      // Negative because we want positive angles to point down
      let verticalAngle = -Math.atan2(direction.y, horizontalDistance);
      
      // Convert to our coordinate system and clamp between 90° down and 20° up
      const minVertical = -Math.PI / 9; // -20 degrees (pointing up)
      const maxVertical = Math.PI / 2; // 90 degrees (pointing down)
      this.targetRotationVertical = Math.max(minVertical, Math.min(maxVertical, verticalAngle));
    }
  }

  /**
   * Update the rotation of all orb groups
   */
  private updateOrbRotation(horizontalRotation: number, verticalRotation: number): void {
    this.orbRotationHorizontal = horizontalRotation;
    this.orbRotationVertical = verticalRotation;
    
    // Apply rotations to all LOD levels
    [this.highDetailOrb, this.mediumDetailOrb, this.lowDetailOrb].forEach(orb => {
      if (orb) {
        orb.rotation.y = horizontalRotation;
        orb.rotation.x = verticalRotation;
      }
    });
  }

  /**
   * Lerp between two angles, handling the wrap-around at 2π
   * @param start Starting angle in radians
   * @param end Target angle in radians
   * @param t Interpolation factor (0-1)
   * @returns Interpolated angle
   */
  private lerpAngle(start: number, end: number, t: number): number {
    // Normalize angles to be between -π and π
    const normalizeAngle = (angle: number) => {
      while (angle > Math.PI) angle -= Math.PI * 2;
      while (angle < -Math.PI) angle += Math.PI * 2;
      return angle;
    };

    let delta = normalizeAngle(end - start);
    
    // Take the shortest path
    if (delta > Math.PI) delta -= Math.PI * 2;
    if (delta < -Math.PI) delta += Math.PI * 2;
    
    // Calculate the lerped value
    return start + delta * t;
  }

  /**
   * Shoot at the target - now with visual projectiles
   */
  private shootAtTarget(target: LODToaster): void {
    // Get the position of the orb center
    const position = this.getPosition().clone();
    const poleHeight = 6.0;
    const barrelLength = 3.0; // Match the barrel length from createDetailLevel
    
    // Adjust to orb center position
    position.y += poleHeight - 0.2; // Orb base position
    
    // Calculate the barrel tip position (projectile spawn point)
    const spawnOffset = new THREE.Vector3(0, 0, barrelLength); // Negative Z to match barrel direction
    
    // Apply rotations in correct order
    spawnOffset.applyAxisAngle(new THREE.Vector3(1, 0, 0), this.orbRotationVertical);
    spawnOffset.applyAxisAngle(new THREE.Vector3(0, 1, 0), this.orbRotationHorizontal);
    
    position.add(spawnOffset);
    
    // Get target position and velocity
    const targetPosition = target.getPosition();
    const targetVelocity = target.getVelocity ? target.getVelocity() : new THREE.Vector3();
    
    // Calculate direction vector - apply lead targeting if target is moving
    let direction;
    
    if (targetVelocity && targetVelocity.lengthSq() > 0.1) {
      // Calculate time for projectile to reach current target position
      const distance = position.distanceTo(targetPosition);
      const timeToTarget = distance / this.projectileSpeed;
      
      // Predict where target will be based on its velocity
      const predictedPosition = targetPosition.clone().add(
        targetVelocity.clone().multiplyScalar(timeToTarget * 0.8)
      );
      
      // Direction to predicted position
      direction = new THREE.Vector3()
        .subVectors(predictedPosition, position)
        .normalize();
    } else {
      // Standard direction for stationary targets
      direction = new THREE.Vector3()
        .subVectors(targetPosition, position)
        .normalize();
    }
    
    // Fire projectile
    if (this.projectileSystem) {
      this.projectileSystem.fireProjectile(
        ProjectileType.BASIC,
        position,
        direction,
        this.projectileSpeed,
        this.projectileDamage
      );
    }
    
    this.lastShotTime = performance.now() / 1000;
  }

  /**
   * Update method called each frame
   */
  public override update(deltaTime: number): void {
    super.update(deltaTime);
    
    // Find target if we don't have one
    if (!this.currentTarget || !this.currentTarget.isActiveState()) {
      this.currentTarget = this.findTargetInRange();
    } else {
      // Check if current target is still in range
      const distanceSq = this.getPosition().distanceToSquared(this.currentTarget.getPosition());
      if (distanceSq > this.targetingRadius * this.targetingRadius) {
        this.currentTarget = null;
      }
    }
    
    // Get current time
    const currentTime = performance.now() / 1000;
    
    // If we have a target, rotate towards it and shoot
    if (this.currentTarget) {
      // Calculate the target rotation angles
      this.rotateOrbTowardsTarget(this.currentTarget);
      
      // Smoothly rotate towards the target (lerp)
      const maxRotationAmount = this.rotationSpeed * deltaTime;
      
      // Calculate angle differences
      const horizontalDifference = Math.abs(this.targetRotationHorizontal - this.orbRotationHorizontal);
      const verticalDifference = Math.abs(this.targetRotationVertical - this.orbRotationVertical);
      
      if (horizontalDifference > 0.01 || verticalDifference > 0.01) {
        // Use lerp to smoothly rotate towards target
        const newHorizontalRotation = this.lerpAngle(
          this.orbRotationHorizontal, 
          this.targetRotationHorizontal, 
          Math.min(1.0, maxRotationAmount / horizontalDifference)
        );
        
        const newVerticalRotation = this.lerpAngle(
          this.orbRotationVertical,
          this.targetRotationVertical,
          Math.min(1.0, maxRotationAmount / verticalDifference)
        );
        
        // Update all orb rotations
        this.updateOrbRotation(newHorizontalRotation, newVerticalRotation);
      }
      
      // Only shoot if we're close enough to the target rotation
      const isAimedAtTarget = horizontalDifference < 0.1 && verticalDifference < 0.1;
      
      // Shoot if cooldown has elapsed and we're aimed at target
      if (isAimedAtTarget && currentTime - this.lastShotTime >= this.shootingInterval) {
        this.shootAtTarget(this.currentTarget);
      }
    }
  }
} 